/**
 * <mega-menu>
 *
 * Wraps one <details> in the header nav row (see sections/header.liquid) and adds
 * the pointer behaviour a retail mega menu needs on top of the native disclosure:
 *
 * - opens on hover after a short intent delay, and switches instantly when another
 *   panel is already open, so sweeping across the nav does not flicker;
 * - closes on pointer-leave after a grace delay, on Escape, on an outside click,
 *   and when focus leaves;
 * - only ever lets one panel be open;
 * - caps the panel height to what is left of the viewport, so a tall menu scrolls
 *   inside itself instead of running off the screen.
 *
 * The top-level item is a real link too (`data-url` on the summary). With a mouse
 * the panel already opens on hover, so a click navigates to that URL. Keyboard
 * activation stays the native toggle, and on touch the first tap opens the panel
 * while a tap on an already open item navigates.
 */

const OPEN_DELAY = 120;
const CLOSE_DELAY = 200;
const HOVER_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 990px)';

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.details ? this.details.querySelector('summary') : null;
    this.panel = this.querySelector('.mega-menu__panel');
    this.timer = null;

    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
  }

  connectedCallback() {
    if (!this.details || !this.summary) return;

    this.hoverMedia = window.matchMedia(HOVER_QUERY);
    this.header = document.querySelector('.header-wrapper');
    this.overlay = this.header ? this.header.querySelector('.header__mega-overlay') : null;

    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);
    this.details.addEventListener('toggle', this.onToggle);
    this.summary.addEventListener('click', this.onSummaryClick);
  }

  disconnectedCallback() {
    this.clearTimer();
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange);
    if (MegaMenu.openInstance === this) MegaMenu.openInstance = null;
    if (!MegaMenu.openInstance) this.hideOverlay();
  }

  get isOpen() {
    return this.details.hasAttribute('open');
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /* A hovered nav item should take over from an already open one with no delay —
     the delay is only there to stop a panel opening as the pointer passes through. */
  onPointerEnter(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!this.hoverMedia.matches) return;

    this.clearTimer();
    if (this.isOpen) return;

    const delay = MegaMenu.openInstance && MegaMenu.openInstance !== this ? 0 : OPEN_DELAY;
    this.timer = setTimeout(() => this.open(), delay);
  }

  onPointerLeave(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!this.hoverMedia.matches) return;

    this.clearTimer();
    this.timer = setTimeout(() => this.close(), CLOSE_DELAY);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyDown(event) {
    if (event.code !== 'Escape' && event.key !== 'Escape') return;
    if (!this.isOpen) return;

    this.close();
    this.summary.focus();
  }

  /* Keyboard activation of a <summary> arrives as a click with `detail === 0`;
     that one keeps the native toggle so Enter/Space still open the panel. */
  onSummaryClick(event) {
    const url = this.summary.dataset.url;
    if (!url) return;
    if (event.detail === 0) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      event.preventDefault();
      window.open(url, '_blank', 'noopener');
      return;
    }

    /* Mouse: hover already opened the panel, so the click goes to the page.
       Touch: the first tap opens the panel, a tap on the open item navigates. */
    if (!this.hoverMedia.matches && !this.isOpen) return;

    event.preventDefault();
    window.location.href = url;
  }

  onDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.close();
  }

  onToggle() {
    if (this.isOpen) {
      if (MegaMenu.openInstance && MegaMenu.openInstance !== this) MegaMenu.openInstance.close();
      MegaMenu.openInstance = this;

      this.summary.setAttribute('aria-expanded', 'true');
      this.setPanelHeight();
      this.showOverlay();
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('resize', this.onViewportChange);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
    } else {
      if (MegaMenu.openInstance === this) MegaMenu.openInstance = null;

      this.summary.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onViewportChange);

      /* Sweeping from one nav item to the next closes this panel while the next one
         is already opening, and <details> fires `toggle` asynchronously — so only
         drop the scrim once nothing is open, or it would flicker between panels. */
      if (!MegaMenu.openInstance) this.hideOverlay();
    }

    /* Keeps the sticky header from sliding away with a panel hanging off it. */
    if (this.header) this.header.preventHide = this.isOpen;
  }

  onViewportChange() {
    if (!this.isOpen) return;
    this.setPanelHeight();
    this.positionOverlay();
  }

  setPanelHeight() {
    if (!this.panel) return;

    const { top } = this.panel.getBoundingClientRect();
    const available = Math.max(240, Math.floor(window.innerHeight - top - 16));
    this.panel.style.setProperty('--mega-menu-max-height', `${available}px`);
  }

  /* The scrim starts where the panel ends, so the header and the panel stay at full
     contrast and only the page behind them greys out. */
  positionOverlay() {
    if (!this.overlay || !this.panel) return;

    const { bottom } = this.panel.getBoundingClientRect();
    const top = Math.min(Math.max(Math.round(bottom), 0), window.innerHeight);
    this.overlay.style.setProperty('--mega-overlay-top', `${top}px`);
  }

  showOverlay() {
    if (!this.overlay) return;
    this.positionOverlay();
    this.overlay.classList.add('is-active');
  }

  hideOverlay() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-active');
  }

  open() {
    this.clearTimer();
    this.details.setAttribute('open', '');
  }

  close() {
    this.clearTimer();
    this.details.removeAttribute('open');
  }
}

MegaMenu.openInstance = null;

customElements.define('mega-menu', MegaMenu);
